import { useEffect, useRef, useState } from 'react'

export interface CopyToClipboardState {
  copied: boolean
  /** True when the clipboard write itself failed (insecure context or permission denied) -- as
   * opposed to simply not-yet-copied. Lets the caller show "Copy failed" instead of a silent
   * no-op, since "Copy" otherwise looks like it worked. */
  copyError: boolean
  copy: () => void
}

/** Copies `text` to the clipboard, resetting `copied`/`copyError` back to false after a delay. */
export function useCopyToClipboard(text: string): CopyToClipboardState {
  const [copied, setCopied] = useState(false)
  const [copyError, setCopyError] = useState(false)
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => () => clearTimeout(resetTimerRef.current), [])

  const copy = () => {
    navigator.clipboard.writeText(text).then(
      () => {
        setCopyError(false)
        setCopied(true)
        clearTimeout(resetTimerRef.current)
        resetTimerRef.current = setTimeout(() => setCopied(false), 2000)
      },
      () => {
        setCopied(false)
        setCopyError(true)
        clearTimeout(resetTimerRef.current)
        resetTimerRef.current = setTimeout(() => setCopyError(false), 3000)
      }
    )
  }

  return { copied, copyError, copy }
}
